import React, { useState } from "react";
import Layout from "../../Layout";
import "./project_case.css";

import VG_Banner from "../../Images/VG_Banner.png";
import VG_Video from "../../Videos/VG_Video.mp4";

export default function VGDatabase() {
  const [active, setActive] = useState("discover");

  function showDiscover() {
    setActive("discover");
  }
  function showWireframes() {
    setActive("wireframes");
  }
  function showDev() {
    setActive("dev");
  }
  function showFinal() {
    setActive("final");
  }

  return (
    <Layout>
      <main className="cs-page">
        {/* 1) FULL WIDTH HERO IMAGE */}
        <section className="cs-hero">
          <img src={VG_Banner} alt="Video Game Database Banner" />
        </section>

        {/* 2) TITLE / TAGLINE / META */}
        <section className="cs-block">
          <h1 className="cs-title">
            {" "}
            <u>Video Game Database</u>
          </h1>
          <p className="cs-tagline">
            My first experience building a full-stack playground that brings
            game data, clean architecture, and responsive UI together.
          </p>

          <div className="cs-divider" />

          <div className="cs-meta">
            <div className="cs-meta-item">
              <h3 className="cs-meta-label">Timeline</h3>
              <p className="cs-meta-text">December 2023 – March 2024</p>
            </div>
            <div className="cs-meta-item">
              <h3 className="cs-meta-label">Programs Used</h3>
              <p className="cs-meta-text">
                Javascript, React.JS, SQL, C#, .NET, Postman, HTML/CSS, Git +
                GitHub
              </p>
            </div>
          </div>
          <div className="cs-divider" />
        </section>

        {/* 3) ABOUT THE PROJECT */}
        <section className="cs-block">
          <h2 className="cs-h2">About the Project</h2>
          <p className="cs-tagline">
            The Video Game Database is a full-stack application that lets users
            browse, search, save, and manage their favorite game titles. Built
            as a semester project, it taught me how to structure a real CRUD
            system—meaning users can Create, Read, Update, and Delete data
            through a clean and simple interface. Behind the scenes, I designed
            a SQL database and built a custom REST API to handle all game and
            user requests, testing each endpoint thoroughly in Postman to ensure
            accuracy and reliability. Once the backend was solid, I connected it
            to a React interface, focusing on basic UI and reusable components
            so the experience still feels smooth.
          </p>
        </section>

        <div className="cs-divider" />

        {/* 4) MY ROLE + ACCOMPLISHMENTS */}
        <section className="cs-block">
          <h2 className="cs-h2">My Role</h2>
          <p className="cs-tagline">
            I designed and built the full-stack foundation of this project,
            handling the database, REST API, and React front-end. My focus was
            on creating a clean system where data flows smoothly and users can
            browse, search, and save games effortlessly.
          </p>

          <div className="cs-card">
            <div class="cs-item">
              <span class="cs-item-title">Database Architecture:</span>
              <span class="cs-item-text">
                Designed a normalized SQL schema to support efficient CRUD
                operations and fast lookups.
              </span>
            </div>

            <div class="cs-item">
              <span class="cs-item-title">REST API Development:</span>
              <span class="cs-item-text">
                Built and tested custom API endpoints using Postman to ensure
                accurate, stable communication across the app.
              </span>
            </div>

            <div class="cs-item">
              <span class="cs-item-title">Front-End Engineering: </span>
              <span class="cs-item-text">
                Developed a responsive React interface with reusable components
                for searching, filtering, and saving games.
              </span>
            </div>

            <div class="cs-item">
              <span class="cs-item-title">State & Data Management:</span>
              <span class="cs-item-text">
                Implemented clean client-side logic for handling user actions,
                API requests, and dynamic UI updates.
              </span>
            </div>

            <div class="cs-item">
              <span class="cs-item-title">Documentation & Setup:</span>
              <span class="cs-item-text">
                Wrote a clear README with instructions, setup steps, and
                technical notes for future developers.
              </span>
            </div>
          </div>
        </section>

        <div className="cs-divider" />

        {/* 6) MY PROCESS: MENU (left) + PANEL (right) */}
        <section className="cs-block">
          <h2 className="cs-h2">My Process</h2>

          <div className="cs-process">
            <div className="cs-menu">
              <button
                className={
                  "cs-menu-item" + (active === "discover" ? " is-active" : "")
                }
                onMouseEnter={showDiscover}
              >
                Concept & Goals
              </button>

              <button
                className={
                  "cs-menu-item" + (active === "wireframes" ? " is-active" : "")
                }
                onMouseEnter={showWireframes}
              >
                Backend Build
              </button>

              <button
                className={
                  "cs-menu-item" + (active === "dev" ? " is-active" : "")
                }
                onMouseEnter={showDev}
              >
                Frontend Build
              </button>

              <button
                className={
                  "cs-menu-item" + (active === "final" ? " is-active" : "")
                }
                onMouseEnter={showFinal}
              >
                Final Outcome
              </button>
            </div>

            <div className="cs-panel">
              {/* DISCOVERY */}
              <div
                className="cs-pane"
                style={{ display: active === "discover" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>Concept & Goals</u>
                </h3>
                <p className="cs-tagline">
                  When I received the assignment for the Video Game Database
                  project, the goal was to build a full-stack application that
                  allowed users to browse, search, and save video game titles.
                  Before writing any code, I spent time outlining the core
                  features, identifying the user actions, and defining what a
                  clean and simple UI should look like. I also mapped out the
                  necessary data relationships so the project would have a
                  strong foundation from the start.
                </p>

                <p className="cs-tagline">
                  I began by sketching database schemas to understand how the
                  backend should be structured. This helped me identify the main
                  entities—games, users, and saved lists—and plan a schema that
                  would prevent duplicate data and make queries efficient. These
                  diagrams became the blueprint for the entire system, guiding
                  how the API would serve data and how the frontend would
                  consume it.
                </p>

                <p className="cs-tagline">
                  Once the architecture was mapped out, I planned the flow of
                  the full stack: how user interactions should reach the API,
                  how the API should respond, and how React would update the UI.
                  With this roadmap, I moved into development knowing exactly
                  how each layer of the app would work together.
                </p>
              </div>

              <div
                className="cs-pane"
                style={{ display: active === "wireframes" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>Backend Build</u>
                </h3>
                <p className="cs-tagline">
                  The backend was built around a custom REST API connected to a
                  normalized SQL database. I structured the schema so that game
                  data could be accessed quickly and cleanly, with proper
                  relationships to support user lists and saved items. Each
                  endpoint was designed to follow CRUD principles—allowing the
                  app to create, read, update, and delete data in a predictable,
                  scalable way.
                </p>

                <p className="cs-tagline">
                  To ensure accuracy and reliability, I tested every API route
                  using Postman. This helped validate data structures, confirm
                  proper request/response formats, and catch errors early. I
                  focused heavily on data consistency—making sure fields were
                  well-defined, responses were sanitized, and no unnecessary
                  data was exposed to the client.
                </p>

                <p className="cs-tagline">
                  Once the core API was stable, I implemented logic to handle
                  search queries, pagination, and error states. These
                  improvements made the backend more efficient and helped create
                  a smooth browsing experience on the frontend. By the time the
                  backend was finished, the app had a strong, predictable data
                  layer that made development of the UI much easier.
                </p>
              </div>

              <div
                className="cs-pane"
                style={{ display: active === "dev" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>Frontend Build</u>
                </h3>
                <p className="cs-tagline">
                  For the frontend, I built a responsive React interface
                  designed to make browsing and searching for games feel
                  effortless. I created reusable components for game cards,
                  search fields, filters, and saved items, which kept the UI
                  consistent and made future adjustments easy. The layout was
                  intentionally minimal—giving users a clean view of the content
                  without visual clutter.
                </p>

                <p className="cs-tagline">
                  To connect everything to the backend, I wrote client-side
                  logic to handle API requests, loading states, and error
                  management. This ensured that searches felt responsive and
                  that the app always communicated clearly with the user, even
                  when data was still loading. I also optimized rendering by
                  organizing state in a way that prevented unnecessary
                  re-renders, improving overall performance.
                </p>

                <p className="cs-tagline">
                  Beyond functionality, I designed the interface so that
                  anyone—regardless of technical background—could intuitively
                  navigate the app. Clean spacing, simple typography, and
                  predictable interactions made the experience smooth from start
                  to finish.
                </p>
              </div>

              {/* FINAL TOUCHES */}
              <div
                className="cs-pane"
                style={{ display: active === "final" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>Final Outcome</u>
                </h3>
                <p className="cs-tagline">
                  The final result was a fully functional full-stack application
                  that brought together clean data, an efficient API, and a
                  simple, user-friendly interface. Users can browse games,
                  search through a growing catalog, and save titles they want to
                  revisit—all supported by reliable backend logic and a polished
                  React front end.
                </p>

                <p className="cs-tagline">
                  This project strengthened my skills across the full
                  development pipeline, from database design to UI building.
                  More importantly, it taught me how to structure an app that is
                  both technically solid and easy for everyday users to enjoy.
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="cs-divider" />

        <section className="cs-block">
          <h2 className="cs-h2">Video Demonstration</h2>
          <div className="cs-video">
            <video src={VG_Video} controls preload="none" muted />
          </div>
        </section>

        <div className="cs-divider" />

        {/* 7) Learning & Impact */}
        <section className="cs-block">
          <h2 className="cs-h2">Learning & Impact</h2>

          <p className="cs-tagline">
            Building the Video Game Database strengthened my full-stack
            development skills, from structuring clean backend systems to
            designing a responsive and intuitive React interface. This project
            taught me how to connect data, logic, and user experience into one
            cohesive application.
          </p>

          <div className="cs-card">
            <div className="cs-item">
              <span className="cs-item-title">Database Architecture:</span>
              <span className="cs-item-text">
                Learned how to design a normalized SQL schema that supports fast
                lookups, clean relationships, and scalable CRUD operations.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">REST API Development:</span>
              <span className="cs-item-text">
                Built custom API endpoints and tested them with Postman,
                improving my understanding of request handling, validation, and
                efficient data flow.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">React Component Design:</span>
              <span className="cs-item-text">
                Developed reusable components for browsing, searching, and
                saving games, enhancing UI consistency and maintainability.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">State & Data Management:</span>
              <span className="cs-item-text">
                Improved my ability to manage application state cleanly, handle
                API interactions, and display loading/error states smoothly.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">User Experience Awareness:</span>
              <span className="cs-item-text">
                Learned how to transform technical features into a clear,
                simple, and welcoming interface.
              </span>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
}
